import { createApi } from "@reduxjs/toolkit/query/react";

import { fromBackendDate } from "@tsp-core/datetime/utils";
import { baseQuery } from "@tsp-core/store/utils";

import { CostAllocationPeriod } from "@tsp-domains/cost-allocation/enums";
import { CustomWorkUuid } from "@tsp-domains/custom-works/types";
import { DepartmentUuid } from "@tsp-domains/departments/types";
import { PositionUuid } from "@tsp-domains/positions/types";
import { WorkItemUuid } from "@tsp-domains/work-items/types";
import { WorkSubTypeUuid, WorkTypeId } from "@tsp-domains/work-types/types";

import { NotificationList, NotificationType, NotificationUuid } from "../types";

export type GetNotificationResponseData = {
  items: Array<{
    uuid: NotificationUuid;
    title: string;
    text: string;
    type: NotificationType;
    metadata: {
      reason: string | null;
      workItemUuid: WorkItemUuid | null;
      customWorkUuid: CustomWorkUuid;
      newCompletedAt?: number;
      newWorkTypeId?: WorkTypeId;
      newWorkSubTypeUuid?: WorkSubTypeUuid;
      newCostAllocationPeriodType?: CostAllocationPeriod;
      newDepartmentUuid?: DepartmentUuid;
      newPositionUuid?: PositionUuid;
      previousCompletedAt?: number;
      previousWorkTypeId?: WorkTypeId;
      previousWorkSubTypeUuid?: WorkSubTypeUuid;
      previousCostAllocationPeriodType?: CostAllocationPeriod;
      previousDepartmentUuid?: DepartmentUuid;
      previousPositionUuid?: PositionUuid;
    };
    createdAt: 1765513661;
  }>;
};

export const notificationsApi = createApi({
  reducerPath: "@notifications/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  keepUnusedDataFor: 0,
  tagTypes: ["NotificationList"],
  endpoints: (builder) => ({
    getNotificationList: builder.query<NotificationList, void, GetNotificationResponseData>({
      query: () => {
        return {
          url: "/notifications",
          method: "GET",
        };
      },
      transformResponse: (response: GetNotificationResponseData): NotificationList => {
        return response.items.map((item) => ({
          uuid: item.uuid,
          createdAt: fromBackendDate(item.createdAt),
          title: item.title,
          text: item.text,
          type: item.type,
          metadata: {
            workItemUuid: item.metadata.workItemUuid,
            customWorkUuid: item.metadata.customWorkUuid,
            newCompletedAt: fromBackendDate(item.metadata.newCompletedAt) ?? null,
            newWorkTypeId: item.metadata.newWorkTypeId ?? null,
            newWorkSubTypeUuid: item.metadata.newWorkSubTypeUuid ?? null,
            newCostAllocationPeriodType: item.metadata.newCostAllocationPeriodType ?? null,
            newDepartmentUuid: item.metadata.newDepartmentUuid ?? null,
            newPositionUuid: item.metadata.newPositionUuid ?? null,
            previousCompletedAt: fromBackendDate(item.metadata.previousCompletedAt) ?? null,
            previousWorkTypeId: item.metadata.previousWorkTypeId ?? null,
            previousWorkSubTypeUuid: item.metadata.previousWorkSubTypeUuid ?? null,
            previousCostAllocationPeriodType: item.metadata.previousCostAllocationPeriodType ?? null,
            previousDepartmentUuid: item.metadata.previousDepartmentUuid ?? null,
            previousPositionUuid: item.metadata.previousPositionUuid ?? null,
            reason: item.metadata.reason,
          },
        }));
      },
      providesTags: ["NotificationList"],
    }),
    deleteNotification: builder.mutation<void, NotificationUuid, void>({
      query: (uuid) => {
        return {
          url: `/notification/${uuid}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["NotificationList"],
    }),
  }),
});
